import classnames from 'classnames';
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { AppConfig } from '../../../../../core-bundle/config/app-config';
import { Message } from '../../Message';
import { MobileWebContainer } from '../../MobileWebContainer';
import { isConsentFlagActive } from '@wetransfer/transfer/component/consent-manager';
import Icon from '@wetransfer/transfer/component/icon';
import ItemList from '@wetransfer/transfer/component/itemlist';
import Scrollable from '@wetransfer/transfer/component/scrollable';
import { convertFromCents } from '@wetransfer/transfer/helper/paidTransfers';
import { useEffectOnce } from '@wetransfer/transfer/hooks/useEffectOnce';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import { isValidTCString } from '@wetransfer/transfer/modules/consent-manager/tc-model';
import I18n from '@wetransfer/transfer/modules/i18n';
import { Button } from '@wetransfer/transfer/oneui/react';
import { createCurrentOrder } from '@wetransfer/transfer/store/helper/currentOrder';
import { selectTCString } from '@wetransfer/transfer/store/selector/consent';
import { selectIsSessionTandC } from '@wetransfer/transfer/store/selector/session';

import styles from './Buy.module.scss';
import {
  BUYER_TERMS_PDF_PATH,
  BUY_BUTTON_LABEL,
  CONTEXT_SHOWN__PURCHASE_WINDOW__EVENT,
  TRANSFER_PURCHASE_FUNNEL__PAYMENT_INITIATED__EVENT,
} from './constants';
import { FooterButton } from './FooterButton';
import { PriceLabel } from './PriceLabel';
import { SellerName } from './SellerName';
import { Title } from './Title';
import { BuyProps } from './types';

export const Buy: React.FC<BuyProps> = ({
  id,
  secret,
  recipient,
  file_id,
  transfer,
  canBuy,
}) => {
  const dispatch = useDispatch();
  const [isBuyButtonDisabled, setIsBuyButtonDisabled] = useState(false);
  const isConsentStringValid = isValidTCString(useSelector(selectTCString));
  const acceptedTandC = useSelector(selectIsSessionTandC);
  const topLevelItemsCount = transfer.itemsTree.items.length;

  let tabIndex;

  // If the user is part of the cookiewall rollout and does not have a valid
  // consent string, this means the user has not interacted with the consent
  // wall and thus we shouldn't let them reach the input button.
  //
  // At this point, the user has already accepted the T and C.
  if (isConsentFlagActive() && !isConsentStringValid) {
    tabIndex = -1;
  }

  // If the user is not part of the cookiewall rollout and has not accepted the T and C,
  // then we shouldn't let the user reach the input button either.
  if (!isConsentFlagActive() && !acceptedTandC) {
    tabIndex = -1;
  }

  useEffectOnce(() =>
    trackAmplitudeEvent(CONTEXT_SHOWN__PURCHASE_WINDOW__EVENT)
  );

  const transferTitle = (
    <Title
      hasMessage={Boolean(transfer.message)}
      content={transfer.displayName}
    />
  );

  const priceLabel = transfer?.price && (
    <PriceLabel
      price={convertFromCents(transfer.price.amount)}
      currency={transfer.price.currency}
    />
  );

  const sellerName = transfer?.seller && (
    <SellerName name={transfer.seller.name} />
  );

  const message = transfer.message ? (
    Browser.isMobile ? (
      <Message content={transfer.message} />
    ) : (
      <div className={styles.messageContainer}>
        <Message content={transfer.message} />
      </div>
    )
  ) : null;

  const handleBuyClick = () => {
    // Track
    trackAmplitudeEvent(TRANSFER_PURCHASE_FUNNEL__PAYMENT_INITIATED__EVENT);
    // Disable button
    setIsBuyButtonDisabled(true);

    // Create order
    if (typeof transfer.id === 'string') {
      dispatch(createCurrentOrder(transfer.id));
    }
  };

  const itemList = (
    <ItemList
      transfer={transfer}
      altText={I18n.t('transfer.pending.subtitle')}
      maxFiles={AppConfig.layout.lists.initialItemsNumber.downloader}
      id={id}
      secret={secret}
      recipient={recipient}
      file_id={file_id}
      hideAction
      previewable={false}
    />
  );

  const terms = (
    <div className={styles.terms}>
      By clicking “Buy Now” you agree to our{' '}
      <a
        className={styles.termsLink}
        href={BUYER_TERMS_PDF_PATH}
        target="_blank"
        rel="noopener noreferrer"
      >
        Supplemental Terms & Privacy Notice
      </a>
    </div>
  );

  if (Browser.isMobile) {
    return (
      <div className="transfer__window downloader buyer">
        <MobileWebContainer
          key="render_download"
          className={classnames(
            'downloader__heading',
            `downloader__heading--${topLevelItemsCount}-files`,
            {
              'downloader__heading--message': Boolean(transfer.message),
              'downloader__heading--receiver-UI': Browser.isMobile,
            }
          )}
          displayName={transferTitle}
          description={sellerName}
          actions={
            <Button variant="primary" className="downloader__download-button">
              <Icon type="Purchase" />
            </Button>
          }
          message={message}
          priceLabel={priceLabel}
        />
        {itemList}
        <FooterButton
          key="transfer_footer_action"
          tabIndex={tabIndex}
          className={classnames(
            'transfer__button',
            'downloader__button-with-icon',
            (isBuyButtonDisabled || !canBuy) && 'transfer__button--inactive'
          )}
          isDisabled={isBuyButtonDisabled || !canBuy}
          onClick={handleBuyClick}
        />
        {terms}
      </div>
    );
  }

  return (
    <div className="transfer__window downloader buyer">
      <Scrollable className={classnames('transfer__contents', styles.contents)}>
        <div
          className={classnames(
            styles.body,
            `downloader__heading downloader__heading--${topLevelItemsCount}-files`
          )}
        >
          {transfer?.seller?.avatar && (
            <div
              className={styles.avatarContainer}
              data-testid="buy_sellerAvatar"
            >
              <img
                alt="seller avatar"
                src={transfer.seller.avatar}
                className={styles.avatar}
              />
            </div>
          )}
          <div className={styles.displayNameContainer}>
            {transferTitle}
            {sellerName}
          </div>
          {priceLabel}
          {message}
        </div>
        {itemList}
      </Scrollable>
      <div className={styles.footer}>
        <Button
          className={styles.downloadButton}
          small
          variant="primary"
          tabindex={tabIndex}
          disabled={isBuyButtonDisabled || !canBuy}
          onClick={handleBuyClick}
        >
          {BUY_BUTTON_LABEL}
        </Button>
        {terms}
      </div>
    </div>
  );
};
